import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCircleCheckBig,
  lucideCircleX,
  lucideClock,
  lucideLoaderCircle,
  lucideOctagonAlert,
} from '@ng-icons/lucide';
import { QrScannerComponent } from 'src/app/pages/public/qr-scanner/qr-scanner.component';
import { orDash } from '../../admin/shared';
import { CheckinV3Service } from './checkin-v3';
import type { CheckinRegistrado } from './checkin-historico';
import {
  resultadoErro,
  resultadoSucesso,
  TONS,
  type Resultado,
  type Tom,
} from './checkin-scanner.estado';

/**
 * Leitura de ingresso pela câmera, em estados sequenciais — o mesmo fluxo do
 * app Flutter (`new_qr_checkin_screen.dart` + `ResultFullscreenDialog`):
 *
 *     lendo → processando → resultado → (Ler próximo) → lendo
 *
 * Três coisas que isto conserta, todas vindas de a câmera nunca parar entre
 * uma leitura e a próxima:
 *
 *  1. **Check-in duplicado.** O decodificador roda a 10fps e o
 *     `app-qr-scanner` emitia a cada 500ms enquanto o QR estivesse na frente
 *     da lente. Do segundo POST em diante o servidor devolve 409
 *     CHECKIN_JA_REALIZADO: o operador lia UM ingresso e via "já utilizado".
 *     São duas defesas independentes, de propósito — o cooldown por código no
 *     scanner (`scan-gate.ts`, que também cobre a tela antiga) e a guarda
 *     `estado() !== 'lendo'` aqui, que não depende de a pausa ter funcionado.
 *  2. **Diálogo de confirmação antes do POST.** Ler o QR JÁ é a confirmação;
 *     o operador apontou a câmera de propósito. O Swal no meio só existia
 *     porque este caminho reusava o do botão da linha.
 *  3. **Duas requisições por leitura.** Recarregar lista e balanço a cada
 *     ingresso é trabalho que ninguém vê — a tela está coberta pela câmera —
 *     e no celular ainda disputa CPU com o decodificador. Voltam UMA vez,
 *     quando o scanner fecha (ver `CheckinPageComponent.fecharScanner`).
 *
 * `@ViewChild` e não `viewChild()` de propósito: a query por signal não
 * resolve no JIT do vitest deste repo (ver `src/app/testing/definir-input.ts`).
 */
@Component({
  selector: 'app-checkin-scanner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [QrScannerComponent, NgIcon],
  providers: [
    provideIcons({
      lucideCircleCheckBig,
      lucideCircleX,
      lucideClock,
      lucideLoaderCircle,
      lucideOctagonAlert,
    }),
  ],
  template: `
    <div
      class="admin-portal-scope"
      [class]="
        mobile()
          ? 'fixed inset-0 z-50 bg-black'
          : 'fixed bottom-5 right-5 z-50 w-[380px] overflow-hidden rounded-card border border-border bg-card shadow-card'
      "
    >
      <!-- A câmera fica MONTADA o tempo todo, inclusive atrás do resultado.
           Desmontar chamaria stop() e a próxima leitura pagaria a
           renegociação do MediaStream — segundos, e um novo pedido de
           permissão em alguns navegadores. Pausar o decodificador custa zero. -->
      <app-qr-scanner
        [isEmbedded]="!mobile()"
        [showHistory]="false"
        [hideControls]="estado() !== 'lendo'"
        title="Leitura de ingresso"
        (scanSuccess)="onCodigoLido($event)"
        (closeScanner)="fechar.emit()"
      />

      <!-- z-[1200] e não z-10: .qr-scanner-fullscreen é position:fixed com
           z-index $zindex-modal + 50, que no Bootstrap 5 dá 1105. O overlay
           ficava ATRÁS da câmera — o operador via o "Scanner paused" do
           html5-qrcode e nenhum sinal de requisição em voo, o que faz a
           espera parecer travamento. -->
      @if (estado() === 'processando') {
        <div
          class="absolute inset-0 z-[1200] flex flex-col items-center justify-center gap-4 bg-black/60"
          data-testid="scanner-processando"
        >
          <ng-icon name="lucideLoaderCircle" size="36px" class="animate-spin text-white" />
          <span class="text-lg font-bold text-white">Processando...</span>
        </div>
      }
    </div>

    <!-- O resultado NÃO mora dentro da moldura do scanner. No desktop aquela
         moldura tem 380px de largura e a altura da prévia da câmera: o cartão
         de dados sobrava num filete rolável de uns 40px, com o nome do
         participante fora da vista. Aqui ele é um diálogo centrado, do tamanho
         do que tem para mostrar; no celular continua ocupando a tela toda,
         como no app. -->
    @if (resultado(); as r) {
      <div
        class="admin-portal-scope fixed inset-0 z-[1210] flex"
        [class]="mobile() ? '' : 'items-center justify-center bg-black/60 p-6'"
        role="alertdialog"
        aria-live="assertive"
        data-testid="scanner-resultado"
      >
        <div
          class="flex min-h-0 flex-col overflow-hidden"
          [class]="mobile() ? 'h-full w-full' : 'max-h-[85vh] w-[26rem] rounded-card shadow-float'"
          [style.background]="cor(r.tom).fundo"
        >
          <div class="shrink-0 px-7 pt-8" [class]="r.linhas.length ? 'pb-7' : 'flex-1 pb-9'">
            <div class="flex flex-col items-center justify-center" [class]="r.linhas.length ? '' : 'h-full'">
              <span
                class="mb-4 flex h-16 w-16 items-center justify-center rounded-[20px]"
                [style.background]="cor(r.tom).badge"
                [style.color]="cor(r.tom).texto"
              >
                <ng-icon [name]="r.icone" size="30px" />
              </span>
              <span
                class="font-display text-center text-[2rem] leading-tight font-bold"
                [style.color]="cor(r.tom).texto"
                data-testid="scanner-titulo"
              >
                {{ r.titulo }}
              </span>
              @if (r.subtitulo) {
                <span
                  class="mt-[0.5rem] text-center text-[1.05rem] leading-snug font-bold opacity-85"
                  [style.color]="cor(r.tom).texto"
                >
                  {{ r.subtitulo }}
                </span>
              }
            </div>
          </div>

          <!-- Espaçamentos de 0.5rem entram como valor arbitrário, não pela
               utility de nome curto: aquele nome colide com o Bootstrap
               legado e o check:collisions cobra. O script manda parar de
               EMITIR o nome, em vez de guardá-lo, quando o lado legado tem
               override !important não-camadado. E ele varre texto puro: um
               comentário que citasse os nomes já os emitiria. -->
          @if (r.linhas.length) {
            <div class="min-h-0 flex-1 overflow-y-auto rounded-t-[28px] bg-card px-6 pt-6 pb-[0.5rem]">
              @for (l of r.linhas; track l.rotulo) {
                <div class="border-t border-border py-3 first:border-t-0">
                  <div class="text-[11px] font-bold tracking-[0.7px] text-muted-foreground uppercase">
                    {{ l.rotulo }}
                  </div>
                  <div
                    class="mt-1 font-semibold break-words text-foreground"
                    [class]="l.destaque ? 'text-[1.2rem]' : 'text-base'"
                  >
                    {{ l.valor }}
                  </div>
                </div>
              }
            </div>
          }

          <!-- Rodapé fixo: nunca some atrás da rolagem do cartão. -->
          <div
            class="shrink-0 px-6 pt-4 pb-6"
            [style.background]="r.linhas.length ? null : cor(r.tom).fundo"
            [class]="r.linhas.length ? 'bg-card' : ''"
          >
            @if (r.traceId) {
              <div class="mb-3 flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2">
                <span class="min-w-0">
                  <span class="block text-[10px] font-bold tracking-[0.7px] text-muted-foreground uppercase">
                    Código de suporte
                  </span>
                  <span class="block truncate font-mono text-xs text-foreground">{{ r.traceId }}</span>
                </span>
                <button
                  type="button"
                  class="shrink-0 rounded-full border border-border px-3 py-1 text-xs font-bold text-foreground"
                  (click)="copiar(r.traceId!)"
                >
                  {{ copiado() ? 'Copiado' : 'Copiar' }}
                </button>
              </div>
            }
            @if (r.prompt) {
              <p class="mb-3 text-center text-[15px] font-bold text-foreground">{{ r.prompt }}</p>
            }
            <div class="flex gap-3">
              @if (r.codigoParaForcar) {
                <button
                  type="button"
                  class="font-display h-[52px] flex-1 rounded-full border-[1.5px] border-border bg-card text-base font-bold text-foreground"
                  (click)="proximo()"
                >
                  Não
                </button>
                <button
                  type="button"
                  class="font-display h-[52px] flex-1 rounded-full bg-[#D91A2A] text-base font-bold text-white"
                  (click)="forcar()"
                >
                  Sim, check-in
                </button>
              } @else if (r.tom === 'ops') {
                <!-- "Ops!" é o único que pode ter sido azar de rede: repetir
                     resolve, e o operador não perde a pessoa da fila. -->
                <button
                  type="button"
                  class="font-display h-[52px] flex-1 rounded-full border-[1.5px] border-white/40 text-base font-bold text-white"
                  (click)="proximo()"
                >
                  Ler próximo
                </button>
                <button
                  type="button"
                  class="font-display h-[52px] flex-1 rounded-full bg-primary text-base font-bold text-primary-foreground"
                  (click)="tentarNovamente()"
                  data-testid="scanner-tentar-novamente"
                >
                  Tentar novamente
                </button>
              } @else {
                <button
                  type="button"
                  class="font-display h-[52px] w-full rounded-full bg-primary text-base font-bold text-primary-foreground"
                  (click)="proximo()"
                  data-testid="scanner-proximo"
                >
                  Ler próximo ingresso
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class CheckinScannerComponent {
  private readonly v3 = inject(CheckinV3Service);

  readonly idEvento = input.required<string>();
  readonly operador = input<string>('');
  readonly mobile = input<boolean>(false);

  /** Um por check-in registrado, para o histórico local da tela — sem ida ao servidor. */
  readonly registrado = output<CheckinRegistrado>();
  readonly fechar = output<void>();

  readonly estado = signal<'lendo' | 'processando' | 'resultado'>('lendo');
  readonly resultado = signal<Resultado | null>(null);
  readonly copiado = signal(false);

  @ViewChild(QrScannerComponent) scanner?: QrScannerComponent;

  /** Última escrita tentada, para o "Tentar novamente" do tom `ops`. */
  private ultimaTentativa: { codigo: string; forcar: boolean } | null = null;

  protected cor(tom: Tom) {
    return TONS[tom];
  }

  onCodigoLido(codigo: string): void {
    // A guarda é o que impede o check-in duplicado: com leitura em voo ou
    // resultado na tela, o mesmo QR pode emitir à vontade que não vira POST.
    if (this.estado() !== 'lendo') return;
    this.estado.set('processando');
    this.scanner?.pausar();
    this.enviar(codigo, false);
  }

  proximo(): void {
    this.resultado.set(null);
    this.copiado.set(false);
    this.estado.set('lendo');
    this.scanner?.retomar();
  }

  forcar(): void {
    const codigo = this.resultado()?.codigoParaForcar;
    if (!codigo) return;
    this.resultado.set(null);
    this.estado.set('processando');
    this.enviar(codigo, true);
  }

  /**
   * Repete a MESMA operação depois de um erro sem tratamento próprio. Vale a
   * pena porque a maioria desses é azar de rede — e a alternativa era mandar o
   * operador pedir o ingresso de novo para quem já está na frente dele.
   *
   * `registrar` gera uma `Idempotency-Key` nova a cada chamada, de propósito:
   * o operador decidiu tentar de novo, então é outra operação.
   */
  tentarNovamente(): void {
    const t = this.ultimaTentativa;
    if (!t) return;
    this.resultado.set(null);
    this.estado.set('processando');
    this.enviar(t.codigo, t.forcar);
  }

  copiar(traceId: string): void {
    void navigator.clipboard?.writeText(traceId).then(() => this.copiado.set(true));
  }

  private enviar(codigo: string, forcar: boolean): void {
    this.ultimaTentativa = { codigo, forcar };
    this.v3.registrar(this.idEvento(), codigo, forcar).subscribe({
      next: (dto) => {
        this.registrado.emit({
          nome: orDash(dto?.participante),
          horario: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
          lote: orDash(dto?.loteNome),
          operador: this.operador(),
        });
        this.mostrar(resultadoSucesso(dto));
      },
      error: (err: unknown) => this.mostrar(resultadoErro(err, codigo, forcar)),
    });
  }

  private mostrar(r: Resultado): void {
    this.resultado.set(r);
    this.estado.set('resultado');
  }
}
